import { GateMovementState2 } from '@/__generated__/graphql';
import {
  AU_BTN_LG_SX,
  AU_BTN_SOLID_ERROR_SX,
  AU_DIVIDER,
  AU_DIVIDER_STRONG,
  AU_RADIUS_CONTROL,
  AU_TEXT,
  auToneSurfaceSx,
} from '@/theme/au-compat';
import { useFormatters } from '@/hooks/useFormatters';
import { useApolloClient } from '@apollo/client';
import { refreshActiveGateData } from '@features/gateCommon/utils/refreshActiveGateData';
import { GateFigure } from '@components/GateFigure';
import { Access, useSuspenseAuthorization } from '@features/authorization';
import { MovementDataRenderer } from '@features/gateCommon';
import type { GateMovementData } from '@features/gateCommon/hooks/useMovementData';
import { useStopGateOperation } from '@features/gateCommon/hooks/useStopGateOperation';
import {
  describeMovementStatus,
  describeQueue,
  getStateColor,
  isFinalizedState,
  isInitializingState,
  overlayStateMap,
  type MovementTone,
} from '@features/gateCommon/utils/gateMovementState';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import { ReactNode, useCallback, useEffect, useState, type FC } from 'react';
import { ProgressBar } from './ProgressBar';
import { OperationPositionData } from './dataComponents/OperationPositionData';

/**
 * La caja de la figura: 170 de ancho como `.oc-monitor-operation-top > .oc-figure`, con `flex: 0 1
 * auto` para que se encoja si el ancho aprieta en vez de empujar a la columna de datos, y la
 * proporción del asset para que reserve su alto antes de que la figura exista.
 *
 * Es el **contenedor**, no la figura — ésa es el incremento 5 (AMARU-888).
 */
const FIGURE_BOX_SX = {
  width: 170,
  aspectRatio: '3030 / 4096',
  flex: '0 1 auto',
} as const;

/** Cada cuánto se re-evalúa el plazo de una solicitud en cola. Es un reloj local, sin red. */
const QUEUED_EXPIRY_TICK_MS = 30_000;

/**
 * La hora actual, re-leída cada `QUEUED_EXPIRY_TICK_MS` **solo mientras `active`**. Sirve para que
 * el texto de una solicitud en cola cambie al pasar el plazo sin recargar. No consulta nada, así
 * que no es polling.
 */
const useNowWhile = (active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), QUEUED_EXPIRY_TICK_MS);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

interface Props {
  movementId: number | null;
}

export const PanelOperation: FC<Props> = ({ movementId }) => {
  const { i18n } = useLingui();
  const { stopOperation, loading } = useStopGateOperation();

  const onStopOperation = useCallback(
    (gateId: string) => {
      stopOperation(gateId);
    },
    [stopOperation],
  );

  /**
   * Memoizado: `MovementDataRenderer` monta `render` como tipo de componente
   * (`<Render {...data} />`), así que una identidad nueva en cada render de `PanelOperation`
   * remonta (y re-suspende) `StopOperationButton` — el mismo riesgo que motivó sacar el hook de
   * suspense a un componente aparte, pero sin esto seguía intacto: el arrow inline igual cambiaba
   * de identidad.
   */
  const renderStopOperationButton = useCallback(
    ({ movement: { state }, gate }: GateMovementData) => (
      <StopOperationButton
        gateId={gate.id}
        finalized={isFinalizedState(state)}
        loading={loading}
        onStopOperation={onStopOperation}
      />
    ),
    [loading, onStopOperation],
  );

  return (
    // `.oc-monitor-operation` — **no lleva card**: el feed y esta área van sobre el cuerpo del
    // diálogo, y las dos que sí son panel son el resumen y el histórico.
    <Box sx={{ gridArea: 'operation', minWidth: 0, padding: '4px 12px 8px' }}>
      {/* `.oc-monitor-operation-top` — la figura y la columna de datos, con 20px de aire. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          justifyContent: 'space-around',
          mb: '12px',
          minWidth: 0,
        }}
      >
        {movementId && (
          <MovementDataRenderer
            movementId={movementId}
            render={({ movement }) => {
              const overlayState = overlayStateMap(movement.state);

              return (
                <GateFigure
                  state={overlayState}
                  boxProps={{ sx: FIGURE_BOX_SX }}
                  position={movement.positions[0]?.[1]}
                  // El backend resuelve el sentido (informado o deducido); `null` = no se sabe.
                  moveDirection={movement.direction}
                />
              );
            }}
            /**
             * El hueco de la figura, **exacto**: sin esto la fila de arriba nacía sin la figura y
             * la columna de datos se corría al centro, para saltar a su sitio cuando el dato
             * llegaba. La proporción es la del asset (`gate-figure.png`, 3030 × 4096), así que el
             * hueco mide lo que va a medir la figura y no un número a ojo que envejece con ella.
             */
            fallback={() => <Box sx={FIGURE_BOX_SX} />}
          />
        )}
        {!movementId && <Box sx={FIGURE_BOX_SX} />}

        {/* `.oc-monitor-operation-side` — la columna de datos: recuadro de posición, rótulo de
            estado, banda de estado y el botón, con 10px entre cada uno. */}
        <Box sx={{ flex: 1, maxWidth: 520, display: 'grid', gap: '10px', minWidth: 0 }}>
          <OperationPositionData
            movementId={movementId}
            render={({ label, position, relativeTime, tone }) => {
              return (
                <Box
                  display="grid"
                  sx={{
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    gap: '14px',
                    // `.oc-position-box`: la caja es **outline** —sólo su línea— y lo único teñido
                    // adentro es el último dato reportado, que es lo que cambia. Antes el valor era
                    // un bloque sólido: el elemento más pesado de la ventana puesto sobre un dato
                    // que se relee cada pocos segundos y que ya está rotulado a su izquierda.
                    border: `1px solid ${AU_DIVIDER}`,
                    borderRadius: AU_RADIUS_CONTROL,
                    padding: '8px 10px',
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, textAlign: 'center' }}>
                    {label}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    sx={{ borderColor: AU_DIVIDER_STRONG, height: 28, alignSelf: 'center' }}
                  />
                  <Box
                    sx={{
                      // Radio de **control** y no de píldora: la caja lleva dos líneas —el número y
                      // su «hace cuánto»— y a ese alto una píldora la vuelve un estadio.
                      ...auToneSurfaceSx(tone, { padding: '4px 16px' }),
                      display: 'grid',
                      gap: '2px',
                      justifyItems: 'center',
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {/* Una sola tinta: las dos líneas heredan la del tono y la jerarquía la llevan
                        el cuerpo y el peso. Las interlíneas son las de `.oc-position-box-value`, que
                        no son las pareadas de la escala: con el alto fijo de la caja, las pareadas
                        la estiran de 36 a 47px. */}
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 13, lineHeight: '14px', color: 'inherit' }}
                    >
                      {position}
                    </Typography>
                    <Typography sx={{ fontSize: 10, lineHeight: '12px', color: 'inherit' }}>
                      {relativeTime}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
          />

          {/* `.oc-monitor-state-label` */}
          <Typography sx={{ fontSize: 12, fontWeight: 700, pl: '4px' }}>
            <Trans>Estado movimiento</Trans>
          </Typography>

          {movementId ? (
            <MovementDataRenderer
              movementId={movementId}
              render={renderOperationState}
              fallback={() => (
                <StateDisplay label={<Trans>Obteniendo información</Trans>} tone="info" />
              )}
            />
          ) : (
            <StateDisplay label={<Trans>Iniciando operación</Trans>} tone="info" />
          )}

          {movementId ? (
            <MovementDataRenderer
              movementId={movementId}
              render={renderStopOperationButton}
              fallback={() => <StopButton disabled />}
            />
          ) : (
            <StopButton disabled />
          )}
        </Box>
      </Box>

      {movementId ? (
        <MovementDataRenderer
          movementId={movementId}
          render={({ movement }) => {
            const progress = movement.operationProgress;
            // `in-queue` **e** `initializing`: las dos son etapas sin progreso todavía, que no es lo
            // mismo que 0%. Antes sólo contaba `in-queue` y la barra de `initializing` se dibujaba
            // clavada en cero.
            const initializing = isInitializingState(movement.state);
            const unknownProgress = !initializing && progress == null;
            const finalized = isFinalizedState(movement.state);
            const { color } = getStateColor(movement.state);

            return (
              <>
                <ProgressBar
                  label={t(i18n)`Progreso de operación`}
                  value={progress ?? 0}
                  speed={finalized ? 0 : 3}
                  indeterminate={initializing || unknownProgress}
                  color={color}
                />
              </>
            );
          }}
          fallback={() => {
            return (
              <ProgressBar
                label={t(i18n)`Progreso de operación`}
                value={0}
                indeterminate
                color={AU_TEXT}
              />
            );
          }}
        />
      ) : (
        <ProgressBar
          label={t(i18n)`Progreso de operación`}
          value={0}
          indeterminate
          color={AU_TEXT}
        />
      )}
    </Box>
  );
};

/**
 * La banda de estado de la operación. Es un componente con identidad estable
 * (`renderOperationState`) porque la solicitud en cola necesita un reloj (`useNowWhile`) para
 * enterarse del plazo sin recargar.
 */
const OperationState: FC<{ movement: GateMovementData['movement'] }> = ({ movement }) => {
  const { i18n } = useLingui();
  const { formatDate } = useFormatters();
  const client = useApolloClient();
  const inQueue = movement.state === GateMovementState2.InQueue;
  const now = useNowWhile(inQueue && movement.expiresAt != null);

  const status = describeMovementStatus(movement, i18n, now);

  // Al pasar el plazo, una lectura: si SGMO alcanzó a despacharla o ya la cerró, el panel lo
  // muestra. Una sola vez por cruce —la dependencia es el booleano—, no polling (D-002/D-010).
  useEffect(() => {
    if (status.pastDeadline) void refreshActiveGateData(client);
  }, [status.pastDeadline, client]);

  // Chip = estado base; debajo, una sola cosa: el hecho y la instrucción de la cola, o el motivo
  // del cierre —también en los cumplidos, que así nombran su causa—.
  const queue = describeQueue(movement, { i18n, now, formatDate });
  const detail = queue
    ? [queue.fact, queue.instruction].filter(Boolean).join(' ')
    : status.close?.title;
  return <StateDisplay label={status.label} detail={detail} tone={status.tone} />;
};

/**
 * Estable a nivel de módulo: `MovementDataRenderer` monta `render` como tipo de componente, y un
 * arrow inline remontaría `OperationState` —y reiniciaría su reloj— en cada render del panel.
 */
const renderOperationState = ({ movement }: GateMovementData) => (
  <OperationState movement={movement} />
);

/**
 * `.oc-monitor-state` — **un estilo y tres tonos**, no tres estilos.
 *
 * Los cinco estados tienen tono, así que no hay caso «sin tono»: la condición que antes pintaba el
 * color sólo en los dos finalizados y dejaba `#34404D` para el resto era exactamente la asimetría
 * que la receta única elimina. Las tres etapas en vuelo comparten `info`, el acromático de la capa
 * — una operación que está pasando no es una noticia ni buena ni mala.
 *
 * `detail` es la segunda línea —la causa del cierre o el aviso de la cola (AMARU-862/US-005)—, en
 * la misma tinta del tono y con menos cuerpo, igual que el `content` de una tarjeta del feed.
 */
const StateDisplay: FC<{ tone: MovementTone; label: ReactNode; detail?: ReactNode }> = ({
  tone,
  label,
  detail,
}) => {
  return (
    <Box sx={{ ...auToneSurfaceSx(tone), textAlign: 'center' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'inherit' }}>{label}</Typography>
      {detail && (
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.35, mt: '1px', color: 'inherit' }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

/**
 * `useSuspenseAuthorization` es un hook de suspense: vive acá, en un componente chico usado solo
 * dentro del `render` de `MovementDataRenderer` (ya dentro de su `Suspense`), y no en el arrow
 * inline que lo invoca — su identidad cambia en cada render de `MovementDataRenderer`, y montarlo
 * ahí remontaría (y re-suspendería) el botón.
 */
const StopOperationButton: FC<{
  gateId: string;
  finalized: boolean;
  loading: boolean;
  onStopOperation: (gateId: string) => void;
}> = ({ gateId, finalized, loading, onStopOperation }) => {
  const { validateAccess } = useSuspenseAuthorization(gateId);
  const canOperate = validateAccess(Access.OperateGate);
  /**
   * Igual que `stopBlocked` en `OperateButtons`: el permiso solo es la causa real del disable
   * cuando la operación sigue en curso. Con `finalized` el botón ya está deshabilitado porque no
   * queda nada que detener, y el tooltip de "no tienes permiso" sería una explicación falsa.
   */
  const permissionBlocked = !finalized && !canOperate;

  return (
    <Tooltip
      title={permissionBlocked ? <Trans>No tienes permiso para operar esta compuerta</Trans> : ''}
    >
      {/* El `span` es para que el tooltip siga recibiendo eventos con el botón deshabilitado; con
          `display: block` hereda el ancho de la celda del grid, que es lo que el `fullWidth` del
          botón necesita para medir. */}
      <span style={{ display: 'block' }}>
        <StopButton
          loading={loading}
          disabled={finalized || !canOperate}
          onClick={() => onStopOperation(gateId)}
        />
      </span>
    </Tooltip>
  );
};

/**
 * «Detener operación» — el sólido de tono `error` de la capa, en tamaño `lg`.
 *
 * **Es el mismo botón que el del widget**, no uno parecido: los dos salen de
 * `AU_BTN_SOLID_ERROR_SX`, que es donde vivían ya los valores que `gateOperate/OperateButtons.tsx`
 * tenía copiados. Lo único que agrega el panel es el tamaño: acá es *la* acción de la vista, en el
 * widget es una de dos.
 *
 * Llevaba `variant="contained" color="error" size="large"` de MUI, o sea el rojo del theme
 * (`#b61f3c`), radio 4 y 15px/500 — otro botón.
 */
const StopButton: FC<{ loading?: boolean; disabled?: boolean; onClick?: () => void }> = (props) => (
  <Button
    {...props}
    fullWidth
    variant="contained"
    color="error"
    sx={{ ...AU_BTN_SOLID_ERROR_SX, ...AU_BTN_LG_SX }}
  >
    <Trans>Detener operación</Trans>
  </Button>
);
